"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteEvent } from "@/hooks/useDeleteModal";
import { useDocumentsModal } from "@/hooks/useDocumentsModal";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";

type OfficialDocument = {
  id: string;
  documentType: string;
  number: string | null;
  title: string;
  description: string;
  date: Date;
  status: string;
  category: string | null;
  documentMediaId: string | null;
};

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const document = row.original as OfficialDocument;
  const setDeleteDocumentId = useDeleteEvent((s) => s.setToDeleteId);
  const setDocumentId = useDocumentsModal((s) => s.setDocumentId);
  const setIsDocumentModalOpen = useDocumentsModal((s) => s.setIsModalOpen);
  const setDefaultValues = useDocumentsModal((s) => s.setDefaultValues);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuItem
          onClick={() => {
            setDocumentId(document.id);
            setDefaultValues({
              documentType: document.documentType as
                | "ordinance"
                | "proclamation",
              number: document.number || "",
              title: document.title,
              description: document.description,
              date: new Date(document.date),
              status: document.status as "current" | "archived" | "proposed",
              category: document.category || "",
              documentMediaId: document.documentMediaId || undefined,
            });
            setIsDocumentModalOpen(true);
          }}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setDeleteDocumentId(document.id);
          }}
        >
          Delete
          <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
